/**
 * Global error boundary — wraps the whole app to prevent any single
 * component crash from white-screening the whole experience.
 *
 * Apple specifically dislikes apps that white-screen during review. This
 * is a defensive net that catches:
 *   • render errors (undefined property access, missing data, etc.)
 *   • lazy-load chunk-load failures (network blip → stuck blank page)
 *
 * Recovery: a "Reload" button reloads the page (which on chunk-load
 * failures will refetch the bundle, and on data errors gives a clean
 * remount). Errors are also reported to the console + analytics.
 */
import { Component } from 'react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[SmartKick ErrorBoundary]', error, info?.componentStack);
    try {
      // Best-effort analytics ping — never let it throw.
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'exception', {
          description: String(error?.message || error).slice(0, 200),
          fatal: false,
        });
      }
    } catch (_) { /* ignore */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch (_) { /* ignore */ }
  };

  handleHome = () => {
    try { window.location.href = '/'; } catch (_) { /* ignore */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#09090b] text-white px-6"
        data-testid="error-boundary"
      >
        <div className="max-w-sm w-full text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-emerald-500/15 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-emerald-400">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 className="text-lg font-black mb-1.5">Something went wrong</h1>
          <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
            We hit a bump on this page. Reloading usually sorts it — your data is safe.
          </p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={this.handleReload}
              className="px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold transition-colors"
              data-testid="error-boundary-reload"
            >
              Reload
            </button>
            <button
              onClick={this.handleHome}
              className="px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-bold transition-colors"
              data-testid="error-boundary-home"
            >
              Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
